import type { UseQueryResult } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";

import { QueryPendingState } from "@/api/QueryPendingState";
import { queryKeys } from "@/api/hooks";
import {
  classifyCandidateContentsFailure,
  type CandidateContentFamily,
  type CandidateContentFile,
  type CandidateContentsFailure,
  type FrozenCandidateContents,
} from "@/api/preview";
import { formatBytes, formatDateTime, shortHash } from "@/lib/format";
import { CLINICAL_CONFIG_SECTIONS, findSection } from "@/shell/navModel";
import { Button } from "@/ui/Button";
import { DefaultNote } from "@/ui/project/DefaultNote";

/**
 * What the clinician is being asked to accept, rendered from the frozen
 * candidate itself (#435).
 *
 * Every line below comes from `GET .../preview-candidates/{candidateId}/contents`
 * and from nothing else. That is the whole point of the surface: acceptance is
 * meant to be the clinical claim that a named person read THIS exact frozen
 * version, and until now the strongest claim the Project page could support was
 * that they had seen its identifier. The draft is deliberately not consulted
 * here — the service already proves the response cannot move after a draft
 * edit, and reading the draft into this panel would reintroduce in the browser
 * the drift the server eliminated.
 *
 * IT DECIDES NOTHING. `PublicationLane` owns the acceptance action, the pinned
 * candidate and the stale comparison; this component renders the response it is
 * handed. The two are split that way because the identity displayed and the
 * identity signed must come from ONE value — the lane holds the query result,
 * passes it here to be rendered and reads the same object when it composes the
 * acceptance request, so there is no second copy for them to disagree about.
 *
 * NO CANDIDATE IDENTIFIER REACHES A URL, here or anywhere below. ADR 0001
 * forbids it, and the identity block therefore renders text a clinician can
 * select and quote, never a link.
 */

/**
 * One sentence per failure kind, exhaustive by `satisfies`.
 *
 * None of them renders the server's own message — #7's rule — and none names an
 * endpoint or a store. What each says is what the clinician can do next, and
 * every one of them is a refusal to accept: a version this surface could not
 * read is a version nobody may sign for.
 */
const CONTENTS_FAILURE_COPY = {
  candidate_unusable:
    "This frozen version can no longer be opened. It has expired, or it is not a version of this project. Freeze a new version, preview it, and accept that one.",
  integrity:
    "This frozen version could not be read and verified, so it cannot be accepted. Either its stored copy did not match what was recorded for it, or the store holding it is unreachable right now. Try again; if it keeps failing, freeze a new version.",
  denied:
    "You are not authorized to read this frozen version, or it is not there. Ask a workspace administrator for access to this project.",
  unavailable:
    "Reading a frozen version is not available in this environment yet. The Editor Service accepts the request but has not implemented it, so there is nothing to retry.",
  error:
    "This frozen version could not be read. Nothing has been assumed about what it contains, and it cannot be accepted until it can be shown.",
} satisfies Record<CandidateContentsFailure["kind"], string>;

/**
 * Retrying is offered for every kind except the three that cannot change.
 *
 * A denial, an unbuilt endpoint and an unusable candidate answer the same way
 * however often they are asked, and a "Try again" beside any of them is a
 * control that cannot work. `candidate_unusable` is the one that reads like an
 * exception and is not: this route serves ONE immutable candidate, and its 422
 * says that candidate has expired or is not this project's, so repeating the
 * identical read is repeating the identical question. The copy above already
 * names the real recovery — freeze a new version — and a button offering the
 * other one would be inviting a clinician to keep pressing at a dead end (PR
 * #482 review). `queryClient.ts` withholds the AUTOMATIC retry on the same
 * fact, so the two halves of "terminal" agree.
 *
 * Everything else — an unreachable store above all — is exactly the condition a
 * second attempt a minute later can clear.
 */
function offersRetry(failure: CandidateContentsFailure): boolean {
  return (
    failure.kind !== "denied" &&
    failure.kind !== "unavailable" &&
    failure.kind !== "candidate_unusable"
  );
}

/**
 * The heading one family of frozen files is read under.
 *
 * A `section` family borrows the label the Project workspace's own navigation
 * uses, through `findSection`, which is what the contract asks for: `section`
 * is "the same slug the Project workspace's navigation and the sections route
 * use, so the inspection and the authoring surface agree on where a file
 * lives". A slug this build has no label for falls back to the slug itself
 * rather than to a blank heading — the registry is server-side and may name a
 * section this browser was built before.
 *
 * `unsectioned` is expected content and is titled as such. The contract
 * documents it as a v0 omission or as pack content newer than the section
 * registry; presenting it as an error would be this surface inventing a fault.
 */
function familyHeading(family: CandidateContentFamily): string {
  if (family.kind === "pack_identity") {
    return "Pack identity";
  }
  if (family.kind === "unsectioned" || !family.section) {
    return "Not claimed by a section";
  }
  return (
    findSection(CLINICAL_CONFIG_SECTIONS, family.section)?.label ??
    family.section
  );
}

/**
 * Why a file's body is not on screen, when it is not.
 *
 * `text` is null for exactly two reasons and the contract states both: the file
 * is `binary`, which this projection does not decode, or it is `yaml`/`text`
 * above the inline cap. Saying which one it is matters more than it looks —
 * rendering either as an empty block would leave a clinician believing they had
 * read a file whose content they were never shown, on a surface whose entire
 * purpose is that they did read it.
 */
function withheldReason(file: CandidateContentFile): string {
  return file.kind === "binary"
    ? "Not shown: this file is not text."
    : "Not shown here: this file is too large to display inline.";
}

function ContentFile({ file }: { file: CandidateContentFile }) {
  const displayName = file.address ?? file.path;
  return (
    <li className="space-y-1.5 rounded-md border border-border p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        {/*
          The dotted address first when the section registry assigns one,
          because it is the spelling the clinician saw while authoring; the
          archive path is always shown beneath it, since that is what the file
          IS. Text only — no identifier here becomes a link.
        */}
        <p className="min-w-0 break-all font-medium text-foreground">
          {displayName}
        </p>
        <p className="font-mono text-xs text-muted-foreground">
          {formatBytes(file.byteLength)}
        </p>
      </div>
      {file.address ? (
        <p className="break-all font-mono text-xs text-muted-foreground">
          {file.path}
        </p>
      ) : null}

      {file.text === null || file.text === undefined ? (
        <p className="text-sm text-muted-foreground">{withheldReason(file)}</p>
      ) : (
        <details className="text-xs">
          <summary
            aria-label={`View contents of ${displayName}`}
            className="cursor-pointer text-muted-foreground"
          >
            View file contents
          </summary>
          {/*
            PRJ-OV-04: raw file bodies are deliberate opt-in detail, not the
            default Project Overview. The frozen bytes remain decoded and
            otherwise untouched. `whitespace-pre-wrap` preserves the YAML a
            clinician recognises, `break-words` bounds long lines, and
            `dir="auto"` lets a Hebrew or mixed-direction body choose its own
            reading direction without changing the surrounding metadata.
          */}
          <pre
            dir="auto"
            className="mt-1.5 max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted/40 p-3 text-xs text-foreground"
          >
            {file.text}
          </pre>
        </details>
      )}

      {/*
        Exact evidence stays available without opening the body. The digest is
        what a release owner reconciles against, while the path, size and hash
        keep the compact row useful even when its raw bytes remain collapsed.
      */}
      <p className="break-all font-mono text-xs text-muted-foreground">
        sha256 {file.sha256}
      </p>
    </li>
  );
}

function FamilyGroup({ family }: { family: CandidateContentFamily }) {
  const heading = familyHeading(family);
  return (
    <section className="space-y-2">
      {/*
        `h3`, not a styled paragraph and not a deeper level. The Publication
        section's own `SectionLabel` is an `h2`, so a family sits directly
        beneath it: a clinician navigating this page by headings should be able
        to jump between the frozen version's families, and a skipped level would
        cost them exactly that.
      */}
      <h3 className="text-sm font-semibold text-foreground">{heading}</h3>
      {family.files.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No files were frozen under this heading.
        </p>
      ) : (
        <ul className="space-y-2">
          {family.files.map((file) => (
            <ContentFile key={file.path} file={file} />
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * The identity of the version on screen, from the response that carried it.
 *
 * This replaces the disclosure that used to read the Project Overview's latest
 * candidate. The two are the same value in the ordinary case and differ in
 * exactly the case #435 is about — a colleague freezing a newer version while
 * this one is being read — and in that case the identity a clinician can see
 * must be the identity of what they are looking at, not of what the overview
 * has moved on to.
 *
 * Text only. ADR 0001 forbids a candidate identifier in a URL.
 */
function ReviewedIdentity({
  contents,
  acceptedCandidateId = null,
}: {
  contents: FrozenCandidateContents;
  /**
   * The candidate the reported acceptance belongs to (#431, surfaced by #436),
   * which is NOT always the one above: a superseded acceptance names an older
   * version that appears nowhere else on the page. This block is where exact
   * identifiers live — the clinician's own flow speaks "version" and never a
   * candidate id, a hash or a sha — so it is where an acceptance becomes
   * auditable.
   *
   * It is read off the ACCEPTANCE axis and off nothing else. "Which version was
   * accepted" and "which version am I reading" are two different questions, and
   * #435 exists because the second one used to be answered by whichever version
   * the project's latest had moved to. Sourcing this row from
   * `ClinicianPublicationStatus.acceptedCandidateId` — server-authoritative
   * acceptance state, the same field `CandidateAcceptanceDetail` reads for its
   * sentence — keeps it out of that dependency: it says what the server records
   * as accepted, never what happens to be on screen.
   */
  acceptedCandidateId?: string | null;
}) {
  return (
    <details className="text-xs">
      <summary className="cursor-pointer text-muted-foreground">
        Support and audit detail
      </summary>
      <dl className="mt-2 space-y-2 rounded-md border border-border bg-muted/30 p-3">
        <div>
          <dt className="font-semibold text-muted-foreground">Version</dt>
          <dd>
            <code className="block select-all break-all text-foreground">
              {contents.candidateId}
            </code>
          </dd>
        </div>
        {acceptedCandidateId ? (
          <div>
            <dt className="font-semibold text-muted-foreground">
              Accepted version
            </dt>
            <dd>
              <code className="block select-all break-all text-foreground">
                {acceptedCandidateId}
              </code>
            </dd>
          </div>
        ) : null}
        <div>
          <dt className="font-semibold text-muted-foreground">Content hash</dt>
          <dd>
            <code className="block select-all break-all text-foreground">
              {contents.contentHash}
            </code>
          </dd>
        </div>
        <div>
          <dt className="font-semibold text-muted-foreground">Base pack</dt>
          <dd>
            <code className="block select-all break-all text-foreground">
              {contents.basePackSha}
            </code>
          </dd>
        </div>
        {contents.revision ? (
          <div>
            <dt className="font-semibold text-muted-foreground">
              Project revision
            </dt>
            <dd>
              <code className="block select-all break-all text-foreground">
                {contents.revision.number}
              </code>
            </dd>
          </div>
        ) : null}
        <div>
          <dt className="font-semibold text-muted-foreground">Frozen at</dt>
          <dd>
            <code className="block select-all break-all text-foreground">
              {contents.createdAt}
            </code>
          </dd>
        </div>
        <div>
          <dt className="font-semibold text-muted-foreground">Expires at</dt>
          <dd>
            <code className="block select-all break-all text-foreground">
              {contents.validity?.expiresAt ?? contents.expiresAt}
            </code>
          </dd>
        </div>
        {contents.validity ? (
          <div>
            <dt className="font-semibold text-muted-foreground">
              Server validity
            </dt>
            <dd>
              <code className="block select-all break-all text-foreground">
                {contents.validity.state}
              </code>
            </dd>
          </div>
        ) : null}
        <div>
          <dt className="font-semibold text-muted-foreground">Eligibility</dt>
          <dd>
            <code className="block select-all break-all text-foreground">
              {contents.eligibility.state}
              {contents.eligibility.state === "preview_only"
                ? ` · ${contents.eligibility.reason}`
                : ""}
            </code>
          </dd>
        </div>
        {contents.archiveSha256 ? (
          <div>
            <dt className="font-semibold text-muted-foreground">
              Archive SHA-256
            </dt>
            <dd>
              <code className="block select-all break-all text-foreground">
                {contents.archiveSha256}
              </code>
            </dd>
          </div>
        ) : null}
        <div>
          <dt className="font-semibold text-muted-foreground">
            Compressed archive size
          </dt>
          <dd>
            <code className="block select-all break-all text-foreground">
              {contents.archiveBytes} bytes
            </code>
          </dd>
        </div>
        {contents.basePackRevision ? (
          <div>
            <dt className="font-semibold text-muted-foreground">
              Base pack revision
            </dt>
            <dd>
              <code className="block select-all break-all text-foreground">
                {contents.basePackRevision}
              </code>
            </dd>
          </div>
        ) : null}
        {contents.baseContentSha256 ? (
          <div>
            <dt className="font-semibold text-muted-foreground">
              Base content SHA-256
            </dt>
            <dd>
              <code className="block select-all break-all text-foreground">
                {contents.baseContentSha256}
              </code>
            </dd>
          </div>
        ) : null}
        <div>
          <dt className="font-semibold text-muted-foreground">
            Validator version
          </dt>
          <dd>
            <code className="block select-all break-all text-foreground">
              {contents.evidence.validatorVersion}
            </code>
          </dd>
        </div>
        <div>
          <dt className="font-semibold text-muted-foreground">
            Validation profile
          </dt>
          <dd>
            <code className="block select-all break-all text-foreground">
              {contents.evidence.validationProfileVersion}
            </code>
          </dd>
        </div>
        <div>
          <dt className="font-semibold text-muted-foreground">
            Guard catalog revision
          </dt>
          <dd>
            <code className="block select-all break-all text-foreground">
              {contents.evidence.guardCatalogRevision}
            </code>
          </dd>
        </div>
        <div>
          <dt className="font-semibold text-muted-foreground">
            Guard catalog SHA-256
          </dt>
          <dd>
            <code className="block select-all break-all text-foreground">
              {contents.evidence.guardCatalogSha256}
            </code>
          </dd>
        </div>
      </dl>
    </details>
  );
}

export function CandidateReview({
  projectId,
  candidateId,
  contents,
  acceptedCandidateId = null,
}: {
  projectId: string;
  /** The candidate being inspected — pinned by the lane, never "the latest". */
  candidateId: string;
  contents: UseQueryResult<FrozenCandidateContents>;
  /**
   * `ClinicianPublicationStatus.acceptedCandidateId` — passed straight through
   * to the identity disclosure, and never used to decide anything here.
   */
  acceptedCandidateId?: string | null;
}) {
  const queryKey = queryKeys.previewCandidateContents(projectId, candidateId);

  if (contents.isPending) {
    return (
      <QueryPendingState
        isPaused={contents.isPaused}
        failureReason={contents.failureReason}
        label="Loading the frozen version"
        errorTitle="This frozen version could not be read"
        rows={3}
        queryKey={queryKey}
      />
    );
  }

  if (contents.isError || !contents.data) {
    const failure = classifyCandidateContentsFailure(contents.error);
    return (
      <div
        role="alert"
        className="space-y-3 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-foreground"
      >
        <p>{CONTENTS_FAILURE_COPY[failure.kind]}</p>
        {/*
          The identifier of the version that could not be read, so a clinician
          reporting the problem can name it. It is the one support coordinate
          worth keeping when the response that would otherwise carry it never
          arrived.
        */}
        <p className="break-all font-mono text-xs text-muted-foreground">
          {candidateId}
        </p>
        {/*
          The server's own message is never rendered — #7's rule. A machine code
          and a request id are what support needs; an unclassified body is what
          must not reach a clinician.
        */}
        {failure.kind === "error" ? (
          <p className="font-mono text-xs text-muted-foreground">
            {failure.code}
            {failure.requestId ? ` · request ${failure.requestId}` : ""}
          </p>
        ) : null}
        {offersRetry(failure) ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => void contents.refetch()}
          >
            <RefreshCw aria-hidden="true" className="h-4 w-4" />
            Try again
          </Button>
        ) : null}
      </div>
    );
  }

  const frozen = contents.data;

  /**
   * The response answered about a different candidate than the one asked for.
   *
   * Unreachable through the contract — the id is a path parameter and the
   * service reads the row by it — which is exactly why it is checked rather
   * than assumed. Every other guarantee on this surface rests on "the identity
   * displayed is the identity signed", and a cache, a proxy or a future
   * refactor putting the wrong object here would break that silently while
   * everything on screen still looked correct. It fails closed and says so; the
   * lane refuses acceptance on the same comparison.
   */
  if (frozen.candidateId !== candidateId) {
    return (
      <div
        role="alert"
        className="space-y-3 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-foreground"
      >
        <p>
          The content that came back is not the version this project asked for,
          so it is not shown and it cannot be accepted. Reload the page and read
          the version again.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void contents.refetch()}
        >
          <RefreshCw aria-hidden="true" className="h-4 w-4" />
          Try again
        </Button>
      </div>
    );
  }

  const files = frozen.families.reduce(
    (total, family) => total + family.files.length,
    0,
  );

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <p className="text-sm text-foreground">
          You are reading the version frozen{" "}
          {formatDateTime(frozen.createdAt)} · {shortHash(frozen.contentHash)}
        </p>
        {/*
          The integrity block is STATED, never branched on. Both of its flags
          are `true` by construction — the contract says a response exists only
          when both checks passed — so treating them as a condition would be
          rendering a branch that can never be taken. What the clinician needs
          to know is that the verification happened, and the counts beside it
          are the response's own.
        */}
        <p className="text-xs text-muted-foreground">
          {`Verified against what was recorded at freeze: ${frozen.integrity.fileCount} file${frozen.integrity.fileCount === 1 ? "" : "s"}, ${formatBytes(frozen.integrity.totalBytes)}.`}
        </p>
        <DefaultNote projectId={projectId} candidateId={candidateId} />
      </div>

      {frozen.families.length === 0 || files === 0 ? (
        <p className="text-sm text-muted-foreground">
          This version contains no files to read. Nothing about it is inferred
          from the current draft — this is what was frozen.
        </p>
      ) : (
        <div className="space-y-4">
          {/*
            The served order, kept: section families in navigation order, then
            the pack's identity documents, then anything no section claims. The
            service partitions once and this surface does not re-sort it — a
            second ordering here would be a second opinion about where a
            clinician should look first.
          */}
          {frozen.families.map((family) => (
            <FamilyGroup
              key={`${family.kind}:${family.section ?? ""}`}
              family={family}
            />
          ))}
        </div>
      )}

      <ReviewedIdentity
        contents={frozen}
        acceptedCandidateId={acceptedCandidateId}
      />
    </div>
  );
}
